"use client";

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useDispatch } from "react-redux";
import { useLazyGetFeatureFlagsQuery } from "@/libs/settings/redux/settings.api";
import TermsFlagsModal from "@/libs/shared/components/FlagsModal/TermsFlagsModal";
import TermsFlagsModalProvider from "@/libs/shared/components/FlagsModal/TermsFlagsModalProvider";
import { FeatureFlagsModel } from "@/libs/settings/redux/settings.types";
import ProcessorFlagsModalProvider from "@/libs/shared/components/FlagsModal/ProcessorFlagsModalProvider";
import ProcessorFlagsModal from "@/libs/shared/components/FlagsModal/ProcessorFlagsModal";
import { clearClientSession } from "@/libs/auth/sessionLogout";
import type { AppDispatch } from "@/libs/shared/store";

type FeatureFlagsContextType = {
  flags?: FeatureFlagsModel["data"];
  isOpenTerms: boolean;
  setIsOpenTerms: (isOpenTerms: boolean) => void;
  isOpenProcessor: boolean;
  setIsOpenProcessor: (isOpenProcessor: boolean) => void;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>(
  {} as FeatureFlagsContextType,
);

type FeatureFlagsProviderProps = {
  children: ReactNode;
};

type ErrorType = {
  status: number;
};

const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
  const pathname = usePathname();
  const [isOpenTerms, setIsOpenTerms] = useState(false);
  const [isOpenProcessor, setIsOpenProcessor] = useState(false);
  const [trigger, result] = useLazyGetFeatureFlagsQuery();

  const dispatch = useDispatch<AppDispatch>();

  const context = useMemo(
    () =>
      ({
        flags: { ...result.data?.data },
        isOpenTerms,
        setIsOpenTerms,
        isOpenProcessor,
        setIsOpenProcessor,
      }) as FeatureFlagsContextType,
    [result, isOpenTerms, isOpenProcessor],
  );

  useEffect(() => {
    void trigger({
      params: pathname?.indexOf("dashboard") > -1 ? "?hs=1" : "",
    });
  }, [trigger, pathname]);

  useEffect(() => {
    if (result.isError && (result.error as ErrorType)?.status === 401) {
      void clearClientSession(dispatch);
    }

    if (result.data) {
      const opened = !result.data.data.terms || !result.data.data.gdpr;
      setIsOpenTerms(opened);
    }
  }, [dispatch, result]);

  return (
    <FeatureFlagsContext.Provider value={context}>
      {children}
      {isOpenTerms && (
        <TermsFlagsModalProvider>
          <TermsFlagsModal />
        </TermsFlagsModalProvider>
      )}
      {isOpenProcessor && (
        <ProcessorFlagsModalProvider>
          <ProcessorFlagsModal />
        </ProcessorFlagsModalProvider>
      )}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlagsContext = () => useContext(FeatureFlagsContext);

export default FeatureFlagsProvider;
